/* eslint-disable */

import {
  Button,
  Input,
  Modal,
  Spin,
  Select,
  Upload,
  Form,
  Radio,
  Input as AntInput,
} from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LoadingOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  _get_biker_by_account_id,
  _onboard_restaurant_biker,
  _upload_image,
} from "../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";

const AddBikerModal = ({
  isModalVisible,
  onCancel,
  restaurantId,
  onBikerAdded,
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Team Selection, 2: User Search, 3: Details & Documents
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddingBiker, setIsAddingBiker] = useState(false);

  const [bikerData, setBikerData] = useState({
    localArea: "",
    vehicleType: "",
    licensePlate: "",
    documents: [],
  });

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  useEffect(() => {
    if (!isModalVisible) {
      setCurrentStep(1);
      setSelectedTeam(null);
      setSearchQuery("");
      setSearchResults(null);
      setSelectedUser(null);
      setBikerData({
        localArea: "",
        vehicleType: "",
        licensePlate: "",
        documents: [],
      });
    }
  }, [isModalVisible]);

  const handleTeamSelection = (team) => {
    setSelectedTeam(team);
    setCurrentStep(2);
  };

  const handleSearchUser = async () => {
    if (!searchQuery) {
      showErrorNotification("Please enter an Account ID or Email.");
      return;
    }
    setIsSearching(true);
    setSearchResults(null);
    setSelectedUser(null);
    try {
      const response = await _get_biker_by_account_id(searchQuery);
      if (response?.data?.data) {
        setSearchResults(response.data.data);
      } else {
        showErrorNotification("User not found.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setCurrentStep(3);
  };

  const handleAddBiker = async () => {
    if (!selectedUser || !selectedTeam) return;

    setIsAddingBiker(true);
    try {
      const onboardingData = {
        userId: selectedUser.id,
      };

      await _onboard_restaurant_biker(restaurantId, onboardingData);
      showSuccessNotification("Biker onboarded successfully!");
      onBikerAdded();
      onCancel();
    } catch (err) {
      showErrorNotification(err.response?.data?.message || err.message);
    } finally {
      setIsAddingBiker(false);
    }
  };

  const renderStep1 = () => (
    <div>
      <h3>Select Biker Team</h3>
      <p>Choose the team this biker will belong to:</p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        <Button
          type="primary"
          size="large"
          onClick={() => handleTeamSelection("RESTAURANT")}
          style={{ height: "60px", textAlign: "left" }}
        >
          <div>
            <strong>Restaurant Team</strong>
            <br />
            <small>Add biker directly to your restaurant team</small>
          </div>
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3>Find User</h3>
      <p>Search for a user to add as a biker in the {selectedTeam} team.</p>

      <div style={{ marginBottom: 16, marginTop: 20 }}>
        <Input
          placeholder="Enter Account ID or Email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onPressEnter={handleSearchUser}
          style={{ width: "calc(100% - 100px)", marginRight: 8 }}
        />
        <Button type="primary" onClick={handleSearchUser} loading={isSearching}>
          Search
        </Button>
      </div>

      {isSearching && <Spin indicator={newAntIcon} />}

      {searchResults && (
        <div
          style={{
            marginTop: 16,
            border: "1px solid #e8e8e8",
            padding: 16,
            borderRadius: 4,
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 12 }}
          >
            <UserOutlined style={{ fontSize: "24px", marginRight: "12px" }} />
            <div>
              <p style={{ margin: 0, fontWeight: "bold" }}>
                {searchResults.firstName} {searchResults.lastName}
              </p>
              <p style={{ margin: 0, color: "#666" }}>{searchResults.email}</p>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <p>
              <strong>Phone:</strong> {searchResults?.phoneCode}
              {searchResults.phoneNumber}
            </p>
            <p>
              <strong>State:</strong> {searchResults?.biker?.state}
            </p>
            <p>
              <strong>Location:</strong> {searchResults?.biker?.lga}
            </p>
          </div>

          <Button
            type="primary"
            onClick={() => handleSelectUser(searchResults)}
          >
            Select This User
          </Button>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3>Biker Details & Documents</h3>
      <p>
        Complete the onboarding for {selectedUser?.firstName}{" "}
        {selectedUser?.lastName}
      </p>

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            backgroundColor: "#f9f9f9",
            borderRadius: 4,
          }}
        >
          <h4>User Details (Pre-filled)</h4>
          <p>
            <strong>Name:</strong> {selectedUser?.firstName}{" "}
            {selectedUser?.lastName}
          </p>
          <p>
            <strong>Email:</strong> {selectedUser?.email}
          </p>
          <p>
            <strong>Phone:</strong> {selectedUser?.phoneCode}
            {selectedUser?.phoneNumber}
          </p>
          <p>
            <strong>Location:</strong> {selectedUser?.lga},{" "}
            {selectedUser?.state}
          </p>
        </div>
      </div>
    </div>
  );

  const renderModalContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  const renderModalFooter = () => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          onClick={() => {
            if (currentStep > 1) {
              setCurrentStep(currentStep - 1);
            } else {
              onCancel();
            }
          }}
        >
          {currentStep === 1 ? "Cancel" : "Back"}
        </Button>

        <div>
          {currentStep === 3 && (
            <Button
              type="primary"
              onClick={handleAddBiker}
              loading={isAddingBiker}
              disabled={isAddingBiker || !selectedUser.email}
            >
              Complete Onboarding
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={`Add New Biker - Step ${currentStep} of 3`}
      open={isModalVisible}
      onCancel={onCancel}
      footer={renderModalFooter()}
      centered={true}
      destroyOnClose={true}
      width={600}
    >
      {renderModalContent()}
    </Modal>
  );
};

export default AddBikerModal;
